import { produce } from "immer";
import { z } from "zod";
import { doubleEncode, makeGetRequest } from "../../utils";

type ListSessionUsersOptions = {
	sessionId: string;
	type?: "live" | "past";
	page_size?: number;
	next_page_token?: string;
};

const AudioCall = z.object({
	call_number: z.string(),
	call_type: z.string(),
	zoom_number: z.string(),
});

export const deviceUnionSchema = z.union([
	z.literal("Phone"),
	z.literal("H.323/SIP"),
	z.literal("Windows"),
	z.literal("Mac"),
	z.literal("iOS"),
	z.literal("Android"),
	z.literal("Web"),
	z.literal("ウェブ"),
	z.string(),
]);

export const netWorkTypeUnionSchema = z.union([
	z.literal("Wired"),
	z.literal("Wifi"),
	z.literal("PPP"),
	z.literal("Cellular"),
	z.literal("Others"),
	z.literal("その他"),
	z.string(),
]);

const SessionUserSchema = z.object({
	id: z.string(),
	name: z.string(),
	device: deviceUnionSchema,
	ip_address: z.string(),
	location: z.string(),
	network_type: netWorkTypeUnionSchema,
	microphone: z.string().nullish(),
	speaker: z.string().nullish(),
	camera: z.string().nullish(),
	data_center: z.string(),
	connection_type: z.string().nullish(),
	join_time: z.string(),
	leave_time: z.string(),
	user_key: z.string().max(100000),
	audio_call: z.array(AudioCall),
});

const resultSchema = z
	.object({
		page_size: z.number(),
		next_page_token: z.string(),
		users: z.array(SessionUserSchema),
	})
	.deepPartial();

type SessionUser = z.infer<typeof resultSchema>;
const listSessionUsers = async (
	token: string,
	options: ListSessionUsersOptions,
): Promise<SessionUser> => {
	const res = await makeGetRequest({
		token,
		pathList: [
			"v2",
			"videosdk",
			"sessions",
			doubleEncode(options.sessionId),
			"users",
		],
		options: produce(options, (draft) => {
			if (!draft.page_size) {
				draft.page_size = 30;
			}
			if (!draft.type) {
				draft.type = "live";
			}
		}),
	});
	return resultSchema.parse(res);
};

export const curriedListSessionUsers = (token: string) => (
	options: ListSessionUsersOptions,
) => listSessionUsers(token, options);
